<script lang="ts">
    import {range} from '../../utils/array-utils';
    import {getLocalization} from 'core/svelte-context/context';
    import type {TREE, NODE} from 'shared/components/kp-generic-hierarchy-trees-view/types';
    import {exists, isNullOrUndefined} from 'shared/utils/custom-utils';

    export let hierarchyTrees: TREE[];
    export let initiallyDisplayedRowsCount = 6;

    const localize = getLocalization();

    let displayAllRows = false;

    function getAllLevelsOfHierarchyTree(hierarchyTree: TREE): number[] {
        const currentNodeLevel = hierarchyTree.nodes.find((node) => node.current)?.level ?? 1;
        const levels = hierarchyTree.nodes.map((node) => node.level).filter((level) => level <= currentNodeLevel);
        return range(1, Math.max(...levels)); // level numbering starts at 1
    }

    function getNodesOnLevel(hierarchyTree: TREE, level: number): NODE[] {
        return hierarchyTree.nodes.filter((node) => node.level === level);
    }

    function getNodesOfPreviousHierarchyTrees(hierarchyTreeIndex: number): NODE[] {
        return hierarchyTrees.slice(0, hierarchyTreeIndex).flatMap((hierarchy) => hierarchy.nodes)
    }

    function getNodesOnLesserLevels(hierarchyTree: TREE, level: number) {
        return hierarchyTree.nodes.filter((node) => node.level < level);
    }

    function getOverallNodeCount(): number {
        return getNodesOfPreviousHierarchyTrees(hierarchyTrees.length).length; // get nodes from all hierarchyTrees [0..length-1]
    }

    // Calculates the index of the current node within the hierarchy tree
    function getCurrentNodeIndex(hierarchyTreeIndex: number, nodeIndex: number, childIndex: number, level: number): number {
        // Gets starting index of current tree
        let currentIndex = getNodesOfPreviousHierarchyTrees(hierarchyTreeIndex).length;

        const currentTree = hierarchyTrees[hierarchyTreeIndex];
        const currentNode = currentTree?.nodes?.find((node) => node.current);

        // If current tree or current node is missing, return the starting index - the data are most likely invalid.
        if (isNullOrUndefined(currentTree) || isNullOrUndefined(currentNode)) {
            throw new Error('Current tree or node with property current=true is missing!');
        }

        // If the provided level is greater than of the 'current node' it counts nodes on lesser levels
        if (level >= currentNode.level) {
            currentIndex += getNodesOnLesserLevels(currentTree, currentNode.level).length;
        }

        // Increment index if provided level is the level of current node's children
        if (level === currentNode.level + 1) {
            currentIndex += 1;
        }

        // If there was a current node before this one, count its children
        const isNodeCurrentBefore = exists(getNodesOnLevel(currentTree, level).slice(0, nodeIndex).find((node) => node.current));
        if (isNodeCurrentBefore) {
            currentIndex += getNodesOnLevel(currentTree, currentNode.level + 1).length;
        }

        // Calculate the final index
        return currentIndex + nodeIndex + childIndex;
    }
</script>

<div class="hierarchy-container">
    {#each hierarchyTrees as hierarchyTree, hierarchyTreeIndex}
        {#each getAllLevelsOfHierarchyTree(hierarchyTree) as level (level)}
            <div class="level-block" style="--left-margin-size: {level - 1}">
                {#each getNodesOnLevel(hierarchyTree, level) as node, nodeIndex}
                    <div class:current-node={node.current} class:hidden={!displayAllRows && getCurrentNodeIndex(hierarchyTreeIndex, nodeIndex, 0, level) >= initiallyDisplayedRowsCount}>
                        <slot {node}></slot>
                    </div>

                    {#if node.current}
                        <div class="level-block" style="--left-margin-size: {level}">
                            {#each getNodesOnLevel(hierarchyTree, node.level + 1) as currentNodeChild, childIndex}
                                <div class:hidden={!displayAllRows && getCurrentNodeIndex(hierarchyTreeIndex, nodeIndex, childIndex, currentNodeChild.level) >= initiallyDisplayedRowsCount}>
                                    <slot node={currentNodeChild}>{currentNodeChild.text}</slot>
                                </div>
                            {/each}
                        </div>
                    {/if}
                {/each}
            </div>
        {/each}
    {/each}
    {#if getOverallNodeCount() > initiallyDisplayedRowsCount}
        <div class="show-all-or-less-button">
            {#if !displayAllRows}
                <span>...</span>
                <button type="button" class="btn btn-default btn-block btn-xs" on:click={() => displayAllRows = true}>
                    {localize(/* @kp-localization commons.ZobrazitVse */ 'commons.ZobrazitVse')}
                </button>
            {:else}
                <button type="button" class="btn btn-default btn-block btn-xs show-less-button" on:click={() => displayAllRows = false}>
                    {localize(/* @kp-localization commons.ShowLess */ 'commons.ShowLess')}
                </button>
            {/if}
        </div>
    {/if}
</div>

<style lang="less">
    .hierarchy-container {
        overflow: auto;
    }

    .level-block {
        margin-left: calc(10px * var(--left-margin-size))
    }

    .current-node {
        text-decoration: underline;
    }

    .show-all-or-less-button {
        display: flex;
        flex-direction: column;
        align-items: center;

        .show-less-button {
            margin-top: 1em
        }
    }
</style>